import React, { useEffect, useState, useCallback } from 'react';
import { Card, Form, Select, Button, message, Tabs } from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';

const { Option } = Select;
const { TabPane } = Tabs;

const SettingsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const projectResponse = await api.get(`/projects/${id}`);
      const projectData = projectResponse.data;
      
      form.setFieldsValue({
        video_ratio: projectData.video_ratio || '16:9',
        video_resolution: projectData.video_resolution || '720P',
        style: projectData.style || '真人写实',
        video_orientation: projectData.video_orientation || '横屏',
      });
    } catch (error) {
      console.error('加载设置失败:', error);
      message.error('加载设置失败');
    } finally {
      setLoading(false);
    }
  }, [id, form]);

  useEffect(() => {
    if (id) {
      loadSettings();
    }
  }, [id, loadSettings]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const projectUpdate = {
        video_ratio: values.video_ratio || '16:9',
        video_resolution: values.video_resolution || '720P',
        style: values.style || '真人写实',
        video_orientation: values.video_orientation || '横屏',
      };
      await api.put(`/projects/${id}`, projectUpdate);
      message.success('设置已保存');
    } catch (error) {
      console.error('保存设置失败:', error);
      message.error('保存设置失败');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToProject = () => {
    navigate(`/project/${id}/script`);
  };

  return (
    <div>
      <Card 
        title="项目设置" 
        loading={loading}
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackToProject}>
            返回项目
          </Button>
        }
      >
        <Form form={form} onFinish={handleSave} layout="vertical">
          <Tabs defaultActiveKey="video_global">
            <TabPane tab="视频全局设置" key="video_global">
              <Form.Item name="video_ratio" label="画面比例">
                <Select>
                  <Option value="16:9">16:9 (横屏)</Option>
                  <Option value="9:16">9:16 (竖屏)</Option>
                  <Option value="1:1">1:1 (方形)</Option>
                  <Option value="4:3">4:3 (标准)</Option>
                </Select>
              </Form.Item>
              
              <Form.Item name="video_resolution" label="默认分辨率">
                <Select>
                  <Option value="480P">480P</Option>
                  <Option value="720P">720P</Option>
                  <Option value="1080P">1080P</Option>
                </Select>
              </Form.Item>
              
              <Form.Item name="style" label="视频风格">
                <Select>
                  <Option value="2D 动漫风格|2d animation style">2D 动漫风格</Option>
                  <Option value="照片级真人超写实|photorealistic, lifelike, ultra detailed">照片级真人超写实</Option>
                  <Option value="3D 国创|Chinese 3D animation style">3D 国创</Option>
                  <Option value="三渲二|cel-shaded">三渲二</Option>
                  <Option value="日式少女漫|shoujo manga style">日式少女漫</Option>
                  <Option value="龙族传说|dragon clan legend art">龙族传说</Option>
                  <Option value="吉卜力|Ghibli style, Studio Ghibli aesthetic">吉卜力</Option>
                  <Option value="80s 年代|1980s retro">80s 年代</Option>
                  <Option value="木叶村|Naruto style, Konohagakure">木叶村</Option>
                  <Option value="名侦探阿楠|Detective Conan style, Case Closed style">名侦探阿楠</Option>
                  <Option value="草帽团|One Piece style, Straw Hat Pirates">草帽团</Option>
                  <Option value="奇幻平涂|fantasy flat illustration">奇幻平涂</Option>
                  <Option value="藤本树|Tatsuki Fujimoto style">藤本树</Option>
                  <Option value="空灵哥特|ethereal gothic">空灵哥特</Option>
                  <Option value="美式 3D|American 3D animation style/ Pixar style">美式 3D</Option>
                  <Option value="怪诞哥特卡通|grotesque gothic cartoon">怪诞哥特卡通</Option>
                  <Option value="比奇堡|SpongeBob SquarePants style, Bikini Bottom">比奇堡</Option>
                  <Option value="复古掌机|retro pixel art, 8-bit graphics">复古掌机</Option>
                  <Option value="美式喜剧|American sitcom aesthetic">美式喜剧</Option>
                  <Option value="史努比|Peanuts comic style">史努比</Option>
                  <Option value="锈湖|Rusty Lake game style">锈湖</Option>
                  <Option value="乐高|LEGO style, brick-built">乐高</Option>
                  <Option value="动森|Animal Crossing style">动森</Option>
                  <Option value="蜡笔小新|Crayon Shin-chan style">蜡笔小新</Option>
                  <Option value="扁平图形设计|flat design, minimal vector art">扁平图形设计</Option>
                  <Option value="莱卡定格动画|Laika stop-motion style">莱卡定格动画</Option>
                  <Option value="日本小人|chibi">日本小人</Option>
                  <Option value="Q 萌马克笔着色|chibi marker art">Q 萌马克笔着色</Option>
                  <Option value="彩色排线手绘|colorful cross-hatching, hand-drawn">彩色排线手绘</Option>
                  <Option value="复古线条都市|retro line art urban">复古线条都市</Option>
                  <Option value="通透光泽马卡龙|glossy translucent macaron colors">通透光泽马卡龙</Option>
                  <Option value="童趣蜡笔插画|childlike crayon illustration">童趣蜡笔插画</Option>
                  <Option value="高能动感卡通|high-energy dynamic cartoon">高能动感卡通</Option>
                  <Option value="梦幻线条怪诞|dreamlike grotesque line art">梦幻线条怪诞</Option>
                  <Option value="火柴人|stick figure">火柴人</Option>
                  <Option value="朦胧暖色速写|hazy warm-toned sketch">朦胧暖色速写</Option>
                  <Option value="复古彩光|vintage chromatic glow">复古彩光</Option>
                  <Option value="粗线超级 Q 版|bold line super deformed">粗线超级 Q 版</Option>
                  <Option value="墨线卡通|ink line cartoon">墨线卡通</Option>
                  <Option value="Q 版 3D|3D chibi">Q 版 3D</Option>
                  <Option value="美式粗线漫画|American bold line comic">美式粗线漫画</Option>
                  <Option value="莫兰蒂 Q 版|Rough granular texture">莫兰蒂 Q 版</Option>
                  <Option value="粗线块卡通|bold shape cartoon">粗线块卡通</Option>
                  <Option value="Q 版草绘|chibi rough sketch">Q 版草绘</Option>
                  <Option value="治愈冒险漫画|healing adventure comic">治愈冒险漫画</Option>
                  <Option value="治愈柔和 Q 版|healing soft chibi">治愈柔和 Q 版</Option>
                  <Option value="可爱马卡龙|cute macaron colors">可爱马卡龙</Option>
                  <Option value="粗线活力动感|bold line vibrant motion">粗线活力动感</Option>
                  <Option value="温暖治愈 Q 版|warm healing chibi">温暖治愈 Q 版</Option>
                  <Option value="毛绒玩具质感|plush toy texture">毛绒玩具质感</Option>
                  <Option value="莫兰迪水粉手绘|Morandi style gouache painting">莫兰迪水粉手绘</Option>
                  <Option value="空气彩铅|airy colored pencil">空气彩铅</Option>
                  <Option value="粗线潮流 Q 版|bold line trendy chibi">粗线潮流 Q 版</Option>
                  <Option value="可爱抽象涂鸦|cute abstract doodle">可爱抽象涂鸦</Option>
                  <Option value="美式颗粒|American comic halftone dots">美式颗粒</Option>
                  <Option value="甜美粉彩|sweet pastel">甜美粉彩</Option>
                  <Option value="童趣速写|childlike sketch">童趣速写</Option>
                  <Option value="像素|pixel art">像素</Option>
                  <Option value="简约扁平手绘|Minimally flat hand drawn">简约扁平手绘</Option>
                  <Option value="活力萌系赛璐璐|vibrant cute cel animation">活力萌系赛璐璐</Option>
                  <Option value="极简几何艺术|minimal geometric art">极简几何艺术</Option>
                  <Option value="矢量扁平色块|vector flat colors">矢量扁平色块</Option>
                  <Option value="清新童趣 Q 版|fresh childish chibi">清新童趣 Q 版</Option>
                  <Option value="空灵现实|ethereal realism">空灵现实</Option>
                  <Option value="方块世界|blocky world, voxel art">方块世界</Option>
                  <Option value="折纸艺术|origami art style">折纸艺术</Option>
                  <Option value="徕卡定格动画|Leica stop motion animation">徕卡定格动画</Option>
                  <Option value="东方古典装饰|eastern classical ornament">东方古典装饰</Option>
                  <Option value="复古梦幻赛璐璐|retro dreamlike cel animation">复古梦幻赛璐璐</Option>
                  <Option value="复古日式|retro Japanese style">复古日式</Option>
                  <Option value="油画釉光|oil painting with glaze finish">油画釉光</Option>
                  <Option value="柔光平涂|soft glow flat colors">柔光平涂</Option>
                  <Option value="低饱和平涂手绘|low saturation flat illustration">低饱和平涂手绘</Option>
                  <Option value="蒸汽波神话糖|vaporwave myth candy">蒸汽波神话糖</Option>
                </Select>
              </Form.Item>
              
              <Form.Item name="video_orientation" label="画面方向">
                <Select>
                  <Option value="横屏">横屏</Option>
                  <Option value="竖屏">竖屏</Option>
                </Select>
              </Form.Item>
            </TabPane>
          </Tabs>

          <Form.Item style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="large">
              保存设置
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default SettingsPage;